import React, { useState } from 'react';
import { View, FlatList, ViewStyle, Pressable, Text } from 'react-native';

import WorkOrderListItem from './work-order-list-item';

import { EnhancedRefreshControl } from '@/components/common/enhanced-refresh-control';
import { useTheme } from '@/components/ui';
import { useWorkOrders } from '@/hooks/use-data';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';
import { workOrderListStyles } from '@/styles';
import type { WorkOrder } from '@/types/models';
import { DAY_MS, dueDayMs, parseDateToMs, todayBusinessMs } from '@/utils/date-utils';
import { compareWorkOrdersByPriorityThen } from '@/utils/work-order-sort';

interface WorkOrdersListProps {
	maxItems?: number;
	currentLocation?: { latitude: number; longitude: number } | null;
	headerComponent?: React.ReactElement;
	footerComponent?: React.ReactElement;
	useFullWidth?: boolean; // New prop to control width constraint
	assignedTo?: string;
	createdBy?: string;
	status?: string;
	excludeApproved?: boolean;
	/**
	 * Filter by where the work order sits in the review chain (#496).
	 *
	 * Prefer this over `excludeApproved` for anything approval-related — that flag cannot
	 * distinguish "accounting has not reviewed it" from "the PM has not approved it", so a
	 * list using it shows PMs raw internal notes the moment maintenance closes a job.
	 *
	 * Implies `status = 'completed'` server-side, which is why `status` is left undefined
	 * when this is set — passing both would AND the default 'open,in_progress' against it
	 * and return nothing.
	 */
	reviewState?: 'needs_review' | 'pending_approval' | 'approved';
	associationPmId?: string;
	/**
	 * Widen `associationPmId` to also match associations with NOBODY assigned.
	 *
	 * Only a super admin can approve a work order on one (`mayApproveWorkOrder`), so only
	 * their queue sets it. Present here because a caller rendering the approval queue's rows
	 * must issue the SAME query the count came from — a list that quietly drops the row the
	 * banner is shouting about is the drift `useApprovalQueue` exists to prevent. Spread
	 * `queue.params` rather than picking fields off it and this stays true by construction.
	 */
	includeUnassigned?: boolean;
	/**
	 * `mine` — the viewer's caseload, resolved by role on the server (assigned-to-me for
	 * maintenance, created-or-in-my-associations for managers, everything for admins).
	 */
	scope?: 'mine';
	/** Only rows due BEFORE today on the Eastern calendar; undated rows are excluded. */
	overdue?: boolean;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	maxDueDays?: number;
	style?: ViewStyle;
	hideIfEmpty?: boolean;
	useFlatList?: boolean;
	associationId?: string;
	showAssignee?: boolean;
}

export const WorkOrdersList: React.FC<WorkOrdersListProps> = ({
	maxItems = 5,
	style,
	hideIfEmpty,
	status = 'open,in_progress',
	createdBy,
	assignedTo,
	excludeApproved = false,
	reviewState,
	associationPmId,
	includeUnassigned,
	scope,
	overdue,
	sortBy = 'created_at',
	sortOrder = 'asc',
	maxDueDays,
	headerComponent,
	footerComponent,
	useFlatList = false, // Default to false to avoid VirtualizedList nesting
	associationId,
	showAssignee = false,
}) => {
	const { refreshing, onRefresh } = usePullToRefresh();
	const theme = useTheme();

	// Fetch work orders assigned to current user
	const { data: workOrdersData } = useWorkOrders({
		assignedTo,
		// `reviewState` already pins status to 'completed'; sending the default
		// 'open,in_progress' alongside it would AND to an empty set.
		status: reviewState ? undefined : (status || 'open,in_progress'),
		reviewState,
		excludeApproved,
		associationPmId,
		// Only sent when true, for the same reason as `overdue` below.
		includeUnassigned: includeUnassigned ? true : undefined,
		associationId,
		createdBy,
		scope,
		// Only sent when true — `overdue=false` would still be the string "false" on the wire.
		overdue: overdue ? true : undefined,
		sortBy,
		sortOrder,
		maxDueDays,
		limit: maxItems > 0 ? maxItems : undefined,
	});

	const allWorkOrders = workOrdersData?.data || [];
	const [showAll, setShowAll] = useState(false);
	const filteredWorkOrders = maxDueDays
		? allWorkOrders.filter((wo) => {
			if (!wo.dueDate) return true;
			const due = dueDayMs(wo.dueDate) ?? Number.NaN;
			const cutoff = todayBusinessMs() + maxDueDays * DAY_MS;
			return due <= cutoff;
		})
		: allWorkOrders;
	const hiddenCount = allWorkOrders.length - filteredWorkOrders.length;
	const workOrders = showAll ? allWorkOrders : filteredWorkOrders;

	if (hideIfEmpty && workOrders.length === 0) {
		return null;
	}

	const getWorkOrderSortTime = (workOrder: WorkOrder): number => {
		const due = dueDayMs(workOrder?.dueDate);
		if (due !== null) return due;
		const created = parseDateToMs(workOrder?.createdAt);
		return created ?? 0;
	};

	// Sort work orders by due date if set, else created date
	const sortSign = sortOrder === 'desc' ? -1 : 1;
	const compareWithinTier = (a: WorkOrder, b: WorkOrder) => sortSign * (getWorkOrderSortTime(a) - getWorkOrderSortTime(b));
	const sortedWorkOrders = [...workOrders].sort((a, b) => compareWorkOrdersByPriorityThen(a, b, compareWithinTier));

	const showHiddenButton = hiddenCount > 0 ? (
		<Pressable
			onPress={() => setShowAll(!showAll)}
			style={{
				padding: 12,
				marginHorizontal: 16,
				marginVertical: 8,
				borderRadius: 8,
				backgroundColor: '#f0f0f0',
				alignItems: 'center',
			}}
		>
			<Text style={{ color: '#007AFF', fontSize: 14, fontWeight: '500' }}>
				{showAll ? 'Hide future work orders' : `Show ${hiddenCount} future work order${hiddenCount === 1 ? '' : 's'}`}
			</Text>
		</Pressable>
	) : null;

	if (useFlatList) {
		return (
			<View style={[{ flex: 1 }, style]}>
				<FlatList
					data={sortedWorkOrders}
					renderItem={({ item }) => <WorkOrderListItem item={item} showAssignee={showAssignee} />}
					keyExtractor={(item) => item.id}
					contentContainerStyle={workOrderListStyles.listContent}
					ListHeaderComponent={headerComponent}
					ListFooterComponent={<>{showHiddenButton}{footerComponent}</>}
					ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
					refreshControl={
						<EnhancedRefreshControl
							refreshing={refreshing}
							onRefresh={onRefresh}
							tintColor={theme.colors.primary}
						/>
					}
				/>
			</View>
		);
	}

	return (
		<View style={style}>
			{headerComponent}
			<View style={workOrderListStyles.listContent}>
				{sortedWorkOrders.map((item, index, arr) => (
					<View key={item.id}>
						<WorkOrderListItem item={item} showAssignee={showAssignee} />
						{index < arr.length - 1 && <View style={{ height: 8 }} />}
					</View>
				))}
			</View>
			{showHiddenButton}
			{footerComponent}
		</View>
	);
};
